import React, { useEffect, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

/**
 * The real ticket PDF, drawn to canvases by pdf.js.
 *
 * WHY NOT THE NATIVE VIEWER, WHICH THIS REPLACES. fe#671 embedded the PDF in
 * an <object> and the native viewer tested clean on iOS. It did not survive
 * real use: on desktop Safari, repeated fast scrolling strands the page
 * content at the bottom of the element with white above it, and then blanks
 * the element permanently — not a dropped frame, a viewer that never comes
 * back. fe#672 gave the element its own background on the theory that the gap
 * was compositing order; it was not, and that fix did not help.
 *
 * Neither failure reproduces on the iOS Simulator (4677 scroll cycles over 32s
 * against the shipped markup, still rendering), and desktop Safari cannot be
 * driven or photographed from here. So this change does NOT rest on a
 * reproduction. It rests on removing the component that fails: there is no
 * native plugin left to lose its backing store. The canvases are ordinary DOM
 * in the normal paint path, so this class of failure is gone by construction
 * rather than by tuning.
 *
 * WHY NOT FieldTicketDisplay EITHER. Still the reason from fe#671: it is a
 * second renderer that has drifted from the PDF, and the review payload it
 * reads has no `crew` key at all while the PDF prints crew, AFE/PO #, UWI and
 * the customer's frozen stamp. This shows the document of record.
 *
 * THE ~1 MB IS ON THIS PAGE ONLY. pdf.js is reached through a dynamic import
 * inside the effect below, so webpack emits it as its own chunk that no other
 * route loads. Importing it at module scope would put it in the main bundle
 * for every screen in the app — check the emitted chunk list if you touch the
 * import.
 *
 * THE LEGACY BUILD ON PURPOSE. `pdfjs-dist/legacy` is transpiled for older
 * engines. Most consultants open these links on a phone, often not a new one,
 * and a viewer that throws on an older iOS is worse than the native one it
 * replaced.
 *
 * EVERY PAGE, NOT JUST THE FIRST. 7 of 133 tickets run past one page. The
 * native viewer at least let you scroll to page 2; a canvas renderer that
 * stops at page 1 would silently hide the rest, which is worse. Pages render
 * sequentially so a long document cannot allocate every backing store at once.
 *
 * NOTHING RECALCULATES SIZE AFTER THE FIRST PAINT, and that is deliberate.
 * Each canvas is given a backing store once and then displayed at 100% width;
 * on rotation the bitmap is scaled by the browser and goes very slightly soft
 * rather than re-rendering. An observer of element dimensions would re-render
 * every page on every frame of a rotation or a scroll-driven layout change,
 * which is how the original complaint started. The basis below is the larger
 * screen edge precisely so the landscape case is already covered at full
 * detail and no such observer is needed.
 */

/** Cap on the backing-store scale. Two is retina; beyond it the memory cost is real and the gain is not. */
const MAX_PIXEL_RATIO = 2;
/** Cap on the CSS width the render is sized for, so a desktop window does not allocate an enormous bitmap. */
const MAX_BASIS_CSS_PX = 1100;

interface TicketPdfViewerProps {
  /** The PDF route for this review token. */
  pdfUrl: string;
  /** Distinguishes the three views of this page in tests and in the DOM. */
  testId?: string;
  /**
   * Cap the viewer at the viewport and scroll the pages inside it, rather than
   * letting a three-page ticket push everything below it three screens down.
   * The awaiting-decision view sets it so the approval controls stay reachable;
   * the read-only views have nothing below the PDF and leave it off.
   */
  fitViewport?: boolean;
}

/**
 * The cap, in two layers. `min()` with `svh` keeps the PDF's bottom edge above
 * the review page's sticky action bar on a phone whose browser toolbar is
 * showing. 13rem is the header and top padding (114px) plus the bar (77px),
 * measured at 390 wide, with ~1rem to spare. An engine
 * without `svh` rejects the whole inline value on assignment, which leaves the
 * class's plain 80vh in force rather than no cap at all.
 */
const FIT_VIEWPORT_MAX_HEIGHT = 'min(80vh, calc(100svh - 13rem))';

type Status = 'loading' | 'ready' | 'failed';

const TicketPdfViewer: React.FC<TicketPdfViewerProps> = ({ pdfUrl, testId = 'ticket-pdf-viewer', fitViewport = false }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    // `cancelled` is read after every await. Without it a token change or an
    // unmount mid-render appends canvases to a host the page has moved on
    // from, and pdf.js keeps a worker alive behind it.
    let cancelled = false;
    let loadingTask: { destroy: () => void } | null = null;

    (async () => {
      setStatus('loading');
      setPageCount(0);
      try {
        const pdfjs: any = await import('pdfjs-dist/legacy/build/pdf');
        const workerMod: any = await import('pdfjs-dist/legacy/build/pdf.worker.entry');
        if (cancelled) return;
        pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default ?? workerMod;

        loadingTask = pdfjs.getDocument({ url: pdfUrl });
        const doc: any = await (loadingTask as any).promise;
        if (cancelled) return;

        const host = hostRef.current;
        if (!host) return;
        host.replaceChildren();

        // Measured once. `clientWidth` can be 0 if the host is not laid out
        // yet, so fall back to the viewport rather than rendering a zero-width
        // page, which produces a canvas that throws.
        const measured = host.clientWidth || window.innerWidth || 800;
        const basis = Math.min(
          Math.max(measured, Math.min(window.screen?.width || 0, MAX_BASIS_CSS_PX)),
          MAX_BASIS_CSS_PX
        );
        const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return;
          const page: any = await doc.getPage(n);
          if (cancelled) return;

          const unscaled = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (basis * ratio) / unscaled.width });

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', `Field ticket PDF, page ${n} of ${doc.numPages}`);
          canvas.setAttribute('data-page', String(n));
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('no 2d context');

          host.appendChild(canvas);
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
        }

        setPageCount(doc.numPages);
        setStatus('ready');
      } catch {
        // Every failure lands the reader on the download, which is the path
        // that worked before any of this and does not depend on our rendering.
        if (!cancelled) setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
      try { loadingTask?.destroy(); } catch { /* already gone */ }
    };
  }, [pdfUrl]);

  return (
    <div
      className="bg-white rounded-xl shadow-sm border overflow-hidden"
      data-testid={`${testId}-card`}
      // The token this viewer is showing, readable from the DOM. The <object>
      // carried it in `data`; without it the "shows the right ticket" guard
      // would have nothing to assert against once rendering moved to canvas.
      data-pdf-url={pdfUrl}
      data-status={status}
      data-page-count={pageCount}
    >
      <div
        data-testid={`${testId}-scroll`}
        className={fitViewport ? 'max-h-[80vh] overflow-y-auto' : undefined}
        style={fitViewport ? { maxHeight: FIT_VIEWPORT_MAX_HEIGHT } : undefined}
      >
      <div ref={hostRef} data-testid={testId} className="bg-white" />

      {status === 'loading' && (
        <div
          className="flex items-center justify-center bg-white"
          style={{ aspectRatio: '8.5 / 11' }}
          data-testid={`${testId}-loading`}
        >
          <Loader2 className="w-6 h-6 text-amber-600 animate-spin" aria-label="Loading the ticket" />
        </div>
      )}

      {status === 'failed' && (
        <div className="p-6 text-center bg-white" data-testid={`${testId}-failed`}>
          <p className="text-gray-600 mb-4">The ticket couldn&apos;t be displayed here.</p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-fallback-download`}
            className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors"
          >
            <Download size={16} />
            Open the ticket PDF
          </a>
        </div>
      )}
      </div>
    </div>
  );
};

export default TicketPdfViewer;
